import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '../../components/typography/typography';
import './dispatch-v2.scss';
import './dispatch-status-cell.scss';
import Icon from '../../components/icons/icon';
import { useSearchParams } from 'react-router-dom';
import { Location } from '../../clients/quipli-service/location/location';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { useDispatchV2Data } from './hooks/use-dispatch-v2-data';
import classNames from 'classnames';
import { FilterToolbar } from './components/filter-toolbar';
import { DispatchTable } from '../dispatch/dispatch-list';
import { DateTime } from 'luxon';
import { Admin } from '../../clients/quipli-service/admin/admin';
import { Dispatch } from '../../clients/quipli-service/order/order';
import { DynamicCalendar, DynamicCalendarColumn, DynamicCalendarItem } from '../dynamic-calendar/dynamic-calendar';
import { AddressCell } from '../order/order-table-cells';
import { DispatchCardModal } from './components/dispatch-card-modal';
import { Spinner } from '../../components/spinner/spinner';
import { Modal } from '../../components/modal/modal';
import { UnassignedDispatches } from './components/unassigned-dispatches';
import { DispatchDirection, DispatchProgress } from '../../clients/quipli-service/dispatch/dispatch';

export const dateTimeFromRequest = (request: DispatchSearchRequest) => {
  let dateValue = DateTime.local();
  const dateStr = request.date;
  if (dateStr) {
    if (dateStr !== 'today') {
      dateValue = DateTime.fromFormat(dateStr, 'MM-dd-yyyy');
    }
  }
  return dateValue;
};

export const driversFromRequest = (request: DispatchSearchRequest, allDrivers: Admin[]) => {
  return allDrivers.filter((d) => request.drivers?.includes(d.adminId.toString()));
};

export const locationsFromRequest = (request: DispatchSearchRequest, allLocations: Location[]): Location[] => {
  if (allLocations.length === 1 || request.locations == undefined) {
    return allLocations;
  }
  return allLocations.filter((l) => request.locations?.includes(l.id.toString()));
};

export class DispatchSearchRequest {
  type?: string;
  date?: string;
  drivers?: string[];
  keyword?: string;
  locations?: string[];
  viewType?: 'Calendar' | 'List';
  hideQuotes?: boolean;
}

export const typeOptions = [
  { value: 'Both', label: 'Both' },
  { value: 'Pickup', label: 'Pickup' },
  { value: 'Delivery', label: 'Delivery' },
];

export const getClassForDispatchStatusLabel = (label: string) => {
  switch (label) {
    case 'PENDING':
      return 'pending';
    case 'OVERDUE':
      return 'scheduled';
    case 'PARTIAL':
    case 'IN_TRANSIT':
      return 'in-transit';
    case 'COMPLETE':
      return 'delivered';
    default:
      return 'default';
  }
};

export const getLabelForDispatchStatus = (status: DispatchProgress, direction: DispatchDirection): string => {
  switch (status) {
    case DispatchProgress.PENDING:
      return direction == DispatchDirection.DELIVERY ? 'Delivery Scheduled' : 'Pickup Scheduled';
    case DispatchProgress.PARTIAL:
      return direction === DispatchDirection.DELIVERY ? 'Partially Delivered' : 'Partially Picked Up';
    case DispatchProgress.IN_TRANSIT:
      return direction == DispatchDirection.DELIVERY ? 'Delivery In Transit' : 'Pickup In Transit';
    case DispatchProgress.COMPLETE:
      return direction == DispatchDirection.DELIVERY ? 'Delivery Complete' : 'Pickup Complete';
    default:
      return 'N/A';
  }
};

const extractDispatchV2Params = (params: URLSearchParams) => {
  return {
    type: params.has('type') ? params.get('type')! : typeOptions[0]?.value,
    date: params.has('date') ? params.get('date')! : 'today',
    drivers: params.has('drivers') ? params.get('drivers')?.split(',') : undefined,
    locations: params.has('locations') ? params.get('locations')?.split(',') : undefined,
    viewType: params.has('viewType') ? (params.get('viewType')! as 'Calendar' | 'List') : 'Calendar',
    hideQuotes: params.has('hideQuotes') ? params.get('hideQuotes') === 'true' : false,
  };
};

export const DispatchV2 = () => {
  const [params] = useSearchParams();
  const [draggingElement, setDraggingElement] = useState<Dispatch | undefined>();

  const {
    allLocations,
    allDrivers,
    dispatch,
    categories,
    toggleCategoryVisibility,
    driversDictionary,
    unitTrackingViaUnitNum,
    assignCard,
    reload,
    loading,
  } = useDispatchV2Data();

  const { request, setRequest } = useRequestQueryParams<DispatchSearchRequest>(
    extractDispatchV2Params(params),
    reload,
    [],
    [],
    750, // The hook needs more than 500 ms for the first debounce unfortunately
  );

  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [clickedOnDispatch, setClickedOnDispatch] = useState<Dispatch | undefined>(undefined);
  const [unassignedModalOpen, setUnassignedModalOpen] = useState(false);

  const handleDragStart = (e: any, item: any) => {
    e.target.classList.add('dragging');
    e.dataTransfer.setData('application/json', JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingElement(item);
  };

  const handleDragEnd = (e: any) => {
    setDraggingElement(undefined);
    e.target.classList.remove('dragging');
  };

  const handleDrop = (e: any, driverId: string | undefined, time: DateTime) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('application/json'));
    assignCard(data, driverId === undefined ? undefined : parseInt(driverId), time, request);
  };

  const selectedDrivers = driversFromRequest(request, allDrivers);
  const selectedLocations = locationsFromRequest(request, allLocations);

  // TODO: ask annika about this! For now just gonna keep it broad
  // const todaysOperatingHours = selectedLocation?.hoursOfOperation?.find(
  //   (h) => h.numericDayOfWeek === selectedDate.weekday,
  // );

  const columns: DynamicCalendarColumn[] = selectedDrivers.map((d) => ({
    columnLabel: <DriverColumn driver={d} />,
    id: d.adminId.toString(),
    label: d.adminFullName,
  }));

  const DraggableCalendarDispatchPiece = ({ item }: { item: Dispatch }) => {
    const [isTextHidden, setIsTextHidden] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleResize = () => {
        if (itemRef.current) {
          const width = itemRef.current.offsetWidth;
          setIsTextHidden(width < 80);
        }
      };

      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    const isDelivery = (item.products.delivery?.length ?? 0) > 0;
    return (
      <div
        ref={itemRef}
        className={classNames('driver-items', isDelivery ? 'delivery' : 'pickup')}
        draggable={true}
        onDragStart={(e) => handleDragStart(e, item)}
        onDragEnd={handleDragEnd}
        onClick={() => {
          setClickedOnDispatch(item);
          setIsCardModalOpen(true);
        }}
      >
        <div className="title-content flex flex-wrap">
          <div className="left-content">
            <Typography type={'text-md'} weight="bold">
              {item.user.name}
            </Typography>

            <Typography type={'text-sm'} weight="regular" className="text-grey-500">
              {`#${item.orderNumber}${!isTextHidden ? ` | ${isDelivery ? 'Delivery' : 'Pickup'}` : ''}`}
            </Typography>
          </div>
          <div className="right-content">
            <div
              className={classNames(
                'status-label dispatch-status-cell whitespace-nowrap',
                getClassForDispatchStatusLabel(item.status!),
              )}
            >
              <Typography type={'text-sm'} weight="medium">
                {getLabelForDispatchStatus(
                  item.status!,
                  isDelivery ? DispatchDirection.DELIVERY : DispatchDirection.PICKUP,
                )}
              </Typography>
            </div>
          </div>
        </div>
        <div className="body-content">
          <AddressCell address={item.address} shortened={isTextHidden} />
          {!isTextHidden && (
            <div className={classNames('avatar-initials extra', isDelivery ? 'delivery' : 'pickup')}>
              <Icon icon="ThinDownload" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const driverItems: DynamicCalendarItem<Dispatch>[] = useMemo(() => {
    return (
      dispatch
        ?.filter(
          (item) =>
            item.driver?.adminId != undefined &&
            item.scheduledTime != undefined &&
            item.startTime != undefined &&
            item.scheduledTime.hasSame(dateTimeFromRequest(request).startOf('day'), 'day'),
        )
        .map((item, index) => {
          return {
            title: index.toString(),
            children: <DraggableCalendarDispatchPiece item={item} />,
            columnId: item.driver!.adminId.toString(),
            draggable: true,
            duration: 60, // TODO: can make this dynamic based on the dispatch type
            startTime: item!.scheduledTime!,
            metadata: item,
          };
        }) ?? []
    );
  }, [dispatch, request]);

  return (
    <div className="container-v2 p-0 sm:p-4 pb-5">
      {selectedLocations && (
        <DispatchCardModal
          isOpen={isCardModalOpen}
          setIsOpen={setIsCardModalOpen}
          allDrivers={allDrivers}
          dispatch={clickedOnDispatch}
          location={allLocations.find((l) => l.id === clickedOnDispatch?.locationId)}
          unitTrackingViaUnitNum={unitTrackingViaUnitNum}
          onReload={() => reload(request)}
        />
      )}

      <Modal isOpen={unassignedModalOpen} close={() => setUnassignedModalOpen(false)} title="Unassigned Dispatches">
        {selectedLocations && (
          <div className="unassigned-modal">
            <UnassignedDispatches
              categories={categories}
              toggleCategoryVisibility={toggleCategoryVisibility}
              reload={reload}
              allLocations={allLocations}
              unitTrackingViaUnitNum={unitTrackingViaUnitNum}
              allDrivers={allDrivers}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
            />
          </div>
        )}
      </Modal>

      <div className="rounded-button" onClick={() => setUnassignedModalOpen(true)}>
        <Typography type={'text-md'}>
          Unassigned Orders ({categories.reduce((acc, c) => acc + c.dispatchCards.length, 0)})
        </Typography>
      </div>

      {loading && (
        <div className="full-page-spinner">
          <Spinner />
        </div>
      )}

      <div className="dispatch-title">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Orders dispatch
        </Typography>
        <ViewTypeSelect
          calendarView={request.viewType!}
          setCalendarView={(viewType) => setRequest((req) => ({ ...req, viewType: viewType }))}
        />
      </div>
      <div className="container-content">
        <FilterToolbar
          request={request}
          setRequest={setRequest}
          allDrivers={allDrivers}
          allLocations={allLocations}
          isLoading={loading}
        />
        <div className={classNames('primary-content', request.viewType)}>
          <div
            className={classNames('left-col', request.viewType)}
            onDrop={(e) => handleDrop(e, undefined, dateTimeFromRequest(request))}
            onDragOver={(e) => {
              e.preventDefault();
            }}
          >
            {request.viewType === 'Calendar' && selectedLocations && (
              <UnassignedDispatches
                categories={categories}
                toggleCategoryVisibility={toggleCategoryVisibility}
                reload={() => reload(request)}
                allLocations={allLocations}
                unitTrackingViaUnitNum={unitTrackingViaUnitNum}
                allDrivers={allDrivers}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
              />
            )}
          </div>
          <div className="right-col">
            {request.viewType === 'Calendar' ? (
              <DynamicCalendar
                columns={columns}
                handleDrop={handleDrop}
                items={driverItems}
                columnLabel="Driver"
                day={dateTimeFromRequest(request)}
                location={selectedLocations.length > 0 ? selectedLocations[0] : undefined} // This could be annoying, but timezone will just be based on the first selected location
                draggingElement={draggingElement}
                isElementDragging={(element: Dispatch) =>
                  draggingElement !== undefined &&
                  element.orderId === draggingElement.orderId &&
                  element.products.delivery?.toString() === draggingElement.products.delivery?.toString() &&
                  element.products.pickup?.toString() === draggingElement.products.pickup?.toString() &&
                  element.scheduledTime?.toString() === draggingElement.scheduledTime?.toString()
                }
                legend={[
                  <div className="dispatch-legend">
                    <div className="circle pickup" />
                    <Typography type={'text-md'}>Pickup</Typography>
                  </div>,
                  <div className="dispatch-legend">
                    <div className="circle delivery" />
                    <Typography type={'text-md'}>Delivery</Typography>
                  </div>,
                ]}
                // endTime={todaysOperatingHours ? todaysOperatingHours.rentalEnd.end : undefined}
              />
            ) : (
              <>
                {dispatch && (
                  <DispatchTable
                    request={{
                      date: dateTimeFromRequest(request),
                    }}
                    drivers={driversDictionary}
                    locations={allLocations}
                    dispatchData={dispatch}
                    setRefresh={() => reload(request)}
                    refresh={false}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewTypeSelect = ({
  calendarView,
  setCalendarView,
}: {
  calendarView: 'Calendar' | 'List';
  setCalendarView: (arg0: 'Calendar' | 'List') => void;
}) => {
  const ViewIconWrapper = ({
    onClick,
    children,
  }: {
    onClick: React.MouseEventHandler<HTMLDivElement>;
    children: React.ReactNode;
  }) => (
    <div className={classNames('cursor-pointer common')} onClick={onClick}>
      {children}
    </div>
  );

  return (
    <div className="view-type-select">
      <div className={classNames('selected', calendarView === 'List' ? 'list' : 'calendar')} />
      <ViewIconWrapper onClick={() => setCalendarView('List')}>
        <Icon icon="ListView" />
      </ViewIconWrapper>
      <ViewIconWrapper onClick={() => setCalendarView('Calendar')}>
        <Icon icon="Calendar" />
      </ViewIconWrapper>
    </div>
  );
};

const DriverColumn = ({ driver }: { driver: Admin }) => {
  return (
    <div className="driver-column">
      <div className="avatar-initials">
        <Typography className="text-grey-500" type="text-sm" weight="semi-bold">
          {driver.adminFullName
            .split(' ')
            .map((part) => part[0])
            .join('')}
        </Typography>
      </div>
      <Typography type="text-md" weight="semi-bold">
        {driver.adminFullName}
      </Typography>
    </div>
  );
};
